import React, { ReactNode } from 'react';
import { ATFCMDetail, InitialNetworkPlanDto } from 'app/model/SOL49';
import { Flex } from 'app/components/common/Flex';
import { Button, Collapse, notification } from 'antd';
import 'app/components/panels/inp/InitialNetworkPlanPanel.less';
import TimeUtils from 'app/common/util/TimeUtils';
import AppContext from 'app/common/context/AppContext';
import { PdfDocument } from 'app/components/panels/inp/pdf/PdfDocument';
import ExtensiblePanel, { ExtensiblePanelProps, ExtensiblePanelState } from 'app/common/panel/ExtensiblePanel';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { NetworkSummaryPanel } from 'app/components/panels/inp/panels/NetworkSummaryPanel';
import { ATFCMDetailsPanel } from 'app/components/panels/inp/panels/ATFCMDetailsPanel';
import memoizeOne from 'memoize-one';
import { NatTracksPanel } from 'app/components/panels/inp/panels/NatTracksPanel';
import NetworkMapPanel from 'app/components/panels/inp/panels/NetworkMapPanel';
import { MilexPanel } from 'app/components/panels/inp/panels/MilexPanel';
import Utils from 'app/common/util/Utils';

interface InitialNetworkPlanPanelProps extends ExtensiblePanelProps {}

interface InitialNetworkPlanPanelState extends ExtensiblePanelState {
    activeKey: string;
    inp: InitialNetworkPlanDto;
    inpPDF: InitialNetworkPlanDto;
    networkMapImg: string;
    natTracksImg: string;
}

const DEFAULT_INP: InitialNetworkPlanDto = {
    date: AppContext.getMonitoredDay(),
    networkSummary: {
        generalInfo: '',
        expectedFlightInfo: '',
        weather: '',
        airspace: {},
        aerodromes: {},
    },
    atfcmDetails: {},
};

export class InitialNetworkPlanPanel extends ExtensiblePanel<
    InitialNetworkPlanPanelProps,
    InitialNetworkPlanPanelState
> {
    static WIDTH = 80;

    constructor(props) {
        super(props);

        this.state = {
            panelIdxVisible: 0,
            showSeparator: true,
            loading: false,
            activeKey: 'map',
            inp: DEFAULT_INP,
            inpPDF: DEFAULT_INP,
            networkMapImg: undefined,
            natTracksImg: undefined,
        };
    }

    componentDidMount() {
        this.refresh();
    }

    private refresh = () => {
        this.setState({
            loading: true,
        });

        ServiceFactory.sol49Service()
            .getInitialNetworkPlan(AppContext.getMonitoredDay().getTime())
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: reply.error.message, description: reply.error.details });
                    console.error(reply.error);
                } else {
                    this.setState({ inp: reply.data, inpPDF: Utils.deepCopy(reply.data) });
                }
            })
            .catch(error => notification.error({ message: 'Error', description: error + '' }))
            .finally(() => this.setState({ loading: false }));
    };

    private onNetworkMapImgUpdate = (networkMapImg: string) => this.setState({ networkMapImg });
    private onNatTracksImgUpdate = (natTracksImg: string) => this.setState({ natTracksImg });

    private updateINP = () => {
        this.setState({
            loading: true,
        });

        ServiceFactory.sol49Service()
            .updateInitialNetworkPlan(this.state.inp)
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: reply.error.message, description: reply.error.details });
                    console.error(reply.error);
                } else {
                    this.setState({
                        inpPDF: Utils.deepCopy(this.state.inp),
                    });
                }
            })
            .catch(error => notification.error({ message: 'Error', description: error + '' }))
            .finally(() => this.setState({ loading: false }));
    };

    mainPanelContent = () => {
        return (
            <Flex direction="vertical" className="initial-network-plan">
                <Flex size={'2em'} direction="vertical" style={{ alignItems: 'center' }}>
                    <p className="title">Initial Network Plan</p>
                    <p>{TimeUtils.formatDateDDMMYYYY(AppContext.getMonitoredDay())}</p>
                </Flex>
                <Collapse
                    accordion
                    activeKey={this.state.activeKey}
                    onChange={(activeKey: string) => this.setState({ activeKey })}>
                    <Collapse.Panel key={'map'} header={'Network MAP'}>
                        <NetworkMapPanel
                            regulations={this.getRegulations(this.state.inp.atfcmDetails)}
                            onMapUpdate={this.onNetworkMapImgUpdate}
                            hotspots={this.getHotspots(this.state.inp.atfcmDetails)}
                        />
                    </Collapse.Panel>
                    <Collapse.Panel key={'summary'} header={'Network Summary'}>
                        <NetworkSummaryPanel inp={this.state.inp} onChange={inp => this.setState({ inp })} />
                    </Collapse.Panel>
                    <Collapse.Panel key={'details'} header={'ATFCM Details'}>
                        <ATFCMDetailsPanel
                            details={this.state.inp.atfcmDetails}
                            onChange={details => this.setState({ inp: { ...this.state.inp, atfcmDetails: details } })}
                        />
                    </Collapse.Panel>
                    <Collapse.Panel key={'milex'} header={'MILEX'}>
                        <MilexPanel
                            milexMap={this.state.inp.milex}
                            onChange={milex => this.setState({ inp: { ...this.state.inp, milex } })}
                        />
                    </Collapse.Panel>
                    <Collapse.Panel key={'nat'} header={'NAT Tracks'}>
                        <NatTracksPanel onNatTracksImgUpdate={this.onNatTracksImgUpdate} />
                    </Collapse.Panel>
                </Collapse>
                <Flex size="2em" direction={'horizontal'} style={{ marginTop: 'auto', marginLeft: 'auto' }}>
                    <Button
                        disabled={this.isEqual(this.state.inp, this.state.inpPDF)}
                        onClick={() => this.updateINP()}
                        style={{ marginRight: '2em' }}>
                        Update
                    </Button>
                    <Button onClick={() => (this.state.panelIdxVisible ? this.hideSidePanel() : this.showSidePanel())}>
                        {this.state.panelIdxVisible ? 'Hide PDF' : 'Show PDF'}
                    </Button>
                </Flex>
            </Flex>
        );
    };

    private isEqual = memoizeOne((inp: InitialNetworkPlanDto, inp2: InitialNetworkPlanDto) =>
        Utils.isEquals(inp, inp2),
    );

    getRegulations = memoizeOne((atfcmDetails: { [p: string]: ATFCMDetail }) => {
        return Object.values(atfcmDetails).flatMap(atfcmDetail => atfcmDetail.regulations);
    });

    getHotspots = memoizeOne((atfcmDetails: { [p: string]: ATFCMDetail }) => {
        return Object.values(atfcmDetails).flatMap(atfcmDetail => atfcmDetail.hotspots);
    });

    sidePanelContent = () => {
        return (
            <Flex grow={true}>
                <PdfDocument
                    inp={this.state.inpPDF}
                    networkMapImg={this.state.networkMapImg}
                    natTracksImg={this.state.natTracksImg}
                />
            </Flex>
        );
    };

    getPanels(): Map<React.ReactNode, number> {
        const panels = new Map<ReactNode, number>();
        panels.set(this.mainPanelContent(), InitialNetworkPlanPanel.WIDTH);
        panels.set(this.sidePanelContent(), InitialNetworkPlanPanel.WIDTH);
        return panels;
    }
}
